/**
 * Neue App-Version laden — von selbst, sobald dabei nichts verloren geht.
 *
 * Bis zum 09.10.2026 wurde nichts angeboten: Der neue Service Worker lag fertig
 * geladen und wartete, bis iOS die App wirklich beendet. Danach gab es einen
 * Knopf, gedrückt hat ihn kaum jemand — und am 26.10.2026 leerte ein Gerät mit
 * alter Fassung die Einkaufsliste beim Server. Seitdem lädt die App neu,
 * sobald es ruhig ist (`safeToReload`): beim Öffnen und bei jeder Rückkehr
 * aus dem Hintergrund. Nie während einer Einheit (der Pausen-Timer ginge
 * verloren) und nie beim Tippen. Der Knopf bleibt für diese Fälle.
 */

import { registerSW } from 'virtual:pwa-register';
import { safeToReload } from './reloadGate.js';
import { markUpdateAvailable, setUpdateCheck } from './updateState.js';

/** Wie oft eine offen gelassene App nach einer neuen Version fragt. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

export function startUpdateCheck(): void {
  let pending: (() => void) | null = null;

  const applyIfSafe = async () => {
    if (pending && (await safeToReload())) pending();
  };

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      pending = () => void updateSW(true);
      markUpdateAvailable(pending);
      void applyIfSafe();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => undefined);
      setUpdateCheck(check);
      // Aus dem Hintergrund zurück heißt auf dem iPhone fast immer „App
      // geöffnet" — der natürliche Moment, nachzusehen und neu zu laden.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        check();
        void applyIfSafe();
      });
      setInterval(check, CHECK_INTERVAL_MS);
    },
  });
}
